import {
  Badge,
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  NumberInput,
  ScrollArea,
  SimpleGrid,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
  Textarea,
  UnstyledButton,
} from "@mantine/core";
import { useDebouncedValue, useMediaQuery } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { api, call, type Entry, type EntryIn, type Food, type Meal } from "../api/client";
import { MEAL_LABELS, MEALS, grams, kcal, parseAmount, scale } from "../lib/format";
import { searchLocally, useFoodIndex } from "../lib/foods";
import { previewEntry, useLogActions } from "../sync/actions";

type Props = {
  opened: boolean;
  onClose: () => void;
  day: string;
  meal: Meal;
  /** Editing an existing entry; otherwise a new one is added. */
  entry?: Entry;
};

function saveFailed(err: unknown) {
  notifications.show({ color: "red", title: "Couldn't save", message: (err as Error).message });
}

export function EntryModal({ opened, onClose, day, meal, entry }: Props) {
  const title = entry ? "Edit entry" : `Add to ${MEAL_LABELS[meal].toLowerCase()}`;
  const phone = useMediaQuery("(max-width: 36em)");
  return (
    <Modal opened={opened} onClose={onClose} title={title} size="lg" fullScreen={phone}>
      {opened && (
        <EntryForm day={day} meal={meal} entry={entry} onDone={onClose} key={entry?.id ?? "new"} />
      )}
    </Modal>
  );
}

function EntryForm({ day, meal: initialMeal, entry, onDone }: Omit<Props, "opened" | "onClose"> & { onDone: () => void }) {
  const [meal, setMeal] = useState<Meal>(entry?.meal ?? initialMeal);
  const [tab, setTab] = useState<string | null>(entry && entry.food_id == null ? "quick" : "search");
  const [food, setFood] = useState<Food | null>(null);
  const editingFood = entry?.food_id != null;

  const existingFood = useQuery({
    queryKey: ["food", entry?.food_id],
    queryFn: () =>
      call(api.GET("/api/v1/foods/{food_id}", { params: { path: { food_id: entry!.food_id! } } })),
    enabled: editingFood,
  });
  // Offline, fall back to the copy of the food saved on the device.
  const index = useFoodIndex();
  const savedFood = index.data?.find((f) => f.id === entry?.food_id) ?? null;
  const chosen = food ?? (editingFood ? (existingFood.data ?? savedFood) : null);

  const mealPicker = (
    <Select
      label="Meal"
      data={MEALS.map((m) => ({ value: m, label: MEAL_LABELS[m] }))}
      value={meal}
      onChange={(v) => v && setMeal(v as Meal)}
      allowDeselect={false}
    />
  );

  if (editingFood && !chosen) {
    return existingFood.isError ? (
      <Text size="sm">This food isn't saved on this device yet. Edit it when you're online.</Text>
    ) : (
      <Loader />
    );
  }

  return (
    <Tabs value={tab} onChange={setTab}>
      {!entry && (
        <Tabs.List grow mb="md">
          <Tabs.Tab value="search">Search foods</Tabs.Tab>
          <Tabs.Tab value="quick">Quick add</Tabs.Tab>
        </Tabs.List>
      )}
      <Tabs.Panel value="search">
        {chosen ? (
          <FoodAmountForm
            food={chosen}
            entry={entry}
            day={day}
            mealPicker={mealPicker}
            meal={meal}
            onBack={entry ? undefined : () => setFood(null)}
            onDone={onDone}
          />
        ) : (
          <FoodSearch onPick={setFood} />
        )}
      </Tabs.Panel>
      <Tabs.Panel value="quick">
        <QuickAddForm entry={entry} day={day} meal={meal} mealPicker={mealPicker} onDone={onDone} />
      </Tabs.Panel>
    </Tabs>
  );
}

function FoodSearch({ onPick }: { onPick: (f: Food) => void }) {
  const [q, setQ] = useState("");
  const [debounced] = useDebouncedValue(q, 250);
  const search = useQuery({
    queryKey: ["foods", "search", debounced],
    queryFn: () => call(api.GET("/api/v1/foods", { params: { query: { q: debounced, limit: 30 } } })),
  });
  const index = useFoodIndex();
  // No connection: search the foods saved on this device instead.
  const offline = search.isError && index.data !== undefined;
  const results = offline ? { data: searchLocally(index.data, debounced), isPending: false } : search;
  return (
    <Stack>
      <TextInput placeholder="Search your foods and the catalog" value={q} onChange={(e) => setQ(e.currentTarget.value)} data-autofocus />
      {offline && (
        <Text size="xs" c="dimmed">
          Offline: searching foods saved on this device.
        </Text>
      )}
      <ScrollArea.Autosize mah="60vh" offsetScrollbars>
        <Stack gap={4}>
          {results.isPending && <Loader size="sm" />}
          {search.isError && !offline && (
            <Text size="sm" c="red">
              {search.error.message}
            </Text>
          )}
          {results.data?.length === 0 && (
            <Text size="sm" c="dimmed">
              No foods found. Use Quick add, or ask your agent to look it up.
            </Text>
          )}
          {results.data?.map((f) => (
            <UnstyledButton key={f.id} onClick={() => onPick(f)} p="xs" style={{ borderRadius: 8 }} className="food-row">
              <Group justify="space-between" wrap="nowrap">
                <div>
                  <Text size="sm" fw={500}>
                    {f.name}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {[f.brand, f.serving_name].filter(Boolean).join(" · ")}
                  </Text>
                </div>
                <Group gap="xs" wrap="nowrap">
                  {f.is_catalog && <Badge size="xs" variant="light" color="gray">catalog</Badge>}
                  <Text size="sm">{kcal(f.kcal)} kcal</Text>
                </Group>
              </Group>
            </UnstyledButton>
          ))}
        </Stack>
      </ScrollArea.Autosize>
    </Stack>
  );
}

function FoodAmountForm({
  food,
  entry,
  day,
  meal,
  mealPicker,
  onBack,
  onDone,
}: {
  food: Food;
  entry?: Entry;
  day: string;
  meal: Meal;
  mealPicker: React.ReactNode;
  onBack?: () => void;
  onDone: () => void;
}) {
  const servingOptions = [
    { value: "ref", label: food.serving_name },
    ...food.servings.map((s) => ({ value: String(s.id), label: s.name })),
    ...(food.serving_grams ? [{ value: "grams", label: "grams (g)" }] : []),
  ];
  const [unit, setUnit] = useState(
    entry?.grams != null ? "grams" : entry?.serving_id != null ? String(entry.serving_id) : "ref",
  );
  const [amount, setAmount] = useState(String(entry?.grams ?? entry?.servings ?? 1));
  const [notes, setNotes] = useState(entry?.notes ?? "");
  const [id] = useState(() => entry?.id ?? crypto.randomUUID());
  const actions = useLogActions();

  const n = parseAmount(amount);
  const multiplier =
    unit === "grams"
      ? 1 / (food.serving_grams ?? 1)
      : unit === "ref"
        ? 1
        : (food.servings.find((s) => String(s.id) === unit)?.multiplier ?? 1);
  const factor = n == null ? null : n * multiplier;

  const submit = () => {
    if (n == null) return;
    const body: EntryIn = {
      date: day,
      meal,
      food_id: food.id,
      notes: notes || null,
      ...(unit === "grams" ? { grams: n } : { servings: n, serving_id: unit === "ref" ? null : Number(unit) }),
    };
    // Saved on the device right away and sent when there's a connection.
    actions.putEntry(id, body, previewEntry(id, body, food, entry)).then(onDone, saveFailed);
  };

  return (
    <Stack>
      <div>
        <Text fw={600}>{food.name}</Text>
        <Text size="sm" c="dimmed">
          {[food.brand, `${kcal(food.kcal)} kcal per ${food.serving_name}`].filter(Boolean).join(" · ")}
        </Text>
      </div>
      <Group grow align="flex-end">
        <Select label="Serving size" data={servingOptions} value={unit} onChange={(v) => v && setUnit(v)} allowDeselect={false} />
        <TextInput label={unit === "grams" ? "Amount (g)" : "Number of servings"} value={amount} onChange={(e) => setAmount(e.currentTarget.value)} inputMode="decimal" error={n == null ? "Enter a number" : null} />
      </Group>
      {mealPicker}
      <Textarea label="Notes" value={notes} onChange={(e) => setNotes(e.currentTarget.value)} autosize minRows={1} />
      {factor != null && (
        <Text size="sm">
          <b>{kcal(scale(food.kcal, factor))} kcal</b> · P {kcal(scale(food.protein_g, factor))} g · C{" "}
          {kcal(scale(food.carbs_g, factor))} g · F {kcal(scale(food.fat_g, factor))} g
        </Text>
      )}
      <Group justify="space-between">
        {onBack ? <Button variant="subtle" onClick={onBack}>Back</Button> : <span />}
        <Button onClick={submit} disabled={n == null}>
          {entry ? "Save" : "Add"}
        </Button>
      </Group>
    </Stack>
  );
}

const NUTRIENT_INPUTS = [
  ["kcal", "Calories"],
  ["protein_g", "Protein (g)"],
  ["carbs_g", "Carbs (g)"],
  ["fat_g", "Fat (g)"],
] as const;
type NutrientKey = (typeof NUTRIENT_INPUTS)[number][0];

/** Like MyFitnessPal: nutrition per serving, times the number of servings eaten. */
function QuickAddForm({
  entry,
  day,
  meal,
  mealPicker,
  onDone,
}: {
  entry?: Entry;
  day: string;
  meal: Meal;
  mealPicker: React.ReactNode;
  onDone: () => void;
}) {
  const actions = useLogActions();
  const [name, setName] = useState(entry?.name ?? "");
  // An existing quick-add entry only has totals, so it's shown as one serving of its amount.
  const [servingSize, setServingSize] = useState(entry?.amount ?? "");
  const [servingGrams, setServingGrams] = useState<number | string>("");
  const [servingsText, setServingsText] = useState("1");
  const [notes, setNotes] = useState(entry?.notes ?? "");
  const [saveFood, setSaveFood] = useState(false);
  const [perServing, setPerServing] = useState<Record<NutrientKey, number | string>>({
    kcal: entry?.kcal ?? "",
    protein_g: entry?.protein_g ?? "",
    carbs_g: entry?.carbs_g ?? "",
    fat_g: entry?.fat_g ?? "",
  });
  const num = (v: number | string | undefined) => (v === "" || v === undefined ? null : Number(v));
  const servings = parseAmount(servingsText);
  const valid = name.trim() !== "" && num(perServing.kcal) != null && servings != null;
  const total = (key: NutrientKey) => {
    const v = num(perServing[key]);
    return v == null || servings == null ? null : scale(v, servings);
  };
  // One id per dialog, so a double tap or retry replaces the same entry instead of adding two.
  const [id] = useState(() => entry?.id ?? crypto.randomUUID());

  const submit = () => {
    if (!valid || servings == null) return;
    const size = servingSize.trim();
    const amount =
      servings === 1 ? size || null : size ? `${servings} × ${size}` : `${servings} servings`;
    const quick: EntryIn = {
      date: day,
      meal,
      name: name.trim(),
      amount,
      notes: notes || null,
      kcal: total("kcal"),
      protein_g: total("protein_g"),
      carbs_g: total("carbs_g"),
      fat_g: total("fat_g"),
      // Keep nutrients the form doesn't show when editing.
      fiber_g: entry?.fiber_g ?? null,
      sugar_g: entry?.sugar_g ?? null,
      saturated_fat_g: entry?.saturated_fat_g ?? null,
      sodium_mg: entry?.sodium_mg ?? null,
    };
    const preview = previewEntry(id, quick, null, entry);
    const queued = saveFood
      ? actions.createFoodAndLog(
          {
            name: name.trim(),
            serving_name: size || "1 serving",
            serving_grams: num(servingGrams),
            kcal: Number(perServing.kcal),
            protein_g: num(perServing.protein_g),
            carbs_g: num(perServing.carbs_g),
            fat_g: num(perServing.fat_g),
            servings: [],
          },
          id,
          { date: day, meal, servings, notes: notes || null },
          preview,
        )
      : actions.putEntry(id, quick, preview);
    queued.then(onDone, saveFailed);
  };

  return (
    <Stack>
      <TextInput label="What" placeholder="e.g. Chicken burrito bowl" value={name} onChange={(e) => setName(e.currentTarget.value)} required data-autofocus />
      <Group grow align="flex-start">
        <TextInput label="Serving size" placeholder="e.g. 1 cup, 1 bowl" value={servingSize} onChange={(e) => setServingSize(e.currentTarget.value)} />
        <TextInput label="Number of servings" value={servingsText} onChange={(e) => setServingsText(e.currentTarget.value)} inputMode="decimal" error={servings == null ? "Enter a number" : null} />
      </Group>
      <Text size="sm" fw={500} mb={-8}>
        Nutrition per serving
      </Text>
      <SimpleGrid cols={{ base: 2, xs: 4 }}>
        {NUTRIENT_INPUTS.map(([key, label]) => (
          <NumberInput
            key={key}
            label={key === "kcal" ? "Calories" : label}
            aria-label={`${label} per serving`}
            min={0}
            value={perServing[key]}
            onChange={(v) => setPerServing({ ...perServing, [key]: v })}
            required={key === "kcal"}
            hideControls
          />
        ))}
      </SimpleGrid>
      {servings != null && servings !== 1 && total("kcal") != null && (
        <Text size="sm" data-testid="quick-total">
          Total for {servings} servings: <b>{kcal(total("kcal"))} kcal</b> · P {grams(total("protein_g"))} · C{" "}
          {grams(total("carbs_g"))} · F {grams(total("fat_g"))}
        </Text>
      )}
      {mealPicker}
      <Textarea label="Notes" value={notes} onChange={(e) => setNotes(e.currentTarget.value)} autosize minRows={1} />
      {!entry && (
        <Group align="flex-end">
          <Checkbox label="Save to My Foods for next time" checked={saveFood} onChange={(e) => setSaveFood(e.currentTarget.checked)} />
          {saveFood && (
            <NumberInput size="xs" label="Serving weight (g), optional" min={0} value={servingGrams} onChange={setServingGrams} hideControls w={180} />
          )}
        </Group>
      )}
      <Group justify="flex-end">
        <Button onClick={submit} disabled={!valid}>
          {entry ? "Save" : "Add"}
        </Button>
      </Group>
    </Stack>
  );
}
